import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { formatViews, getCategoryName } from "@/utils/content";

export default function ArticleSidebar({ article, related }: { article: Article; related: Article[] }) {
  const headings = article.content.filter((b) => b.type === "h2");

  return (
    <aside className="flex flex-col gap-5 lg:sticky lg:top-24 lg:self-start">
      <div className="rounded-lg border border-background-200 bg-background-100 p-5">
        <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
          <i className="ri-folder-open-line text-lg text-secondary-600"></i>
          事件概览
        </h2>
        <dl className="mt-4 space-y-3 text-sm">
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">分类</dt>
            <dd>
              <Link to={`/topics?cat=${article.categoryId}`} className="text-secondary-700 transition-colors hover:text-primary-600 cursor-pointer">
                {getCategoryName(article.categoryId)}
              </Link>
            </dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">整理时间</dt>
            <dd className="text-foreground-800">{article.date}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">阅读</dt>
            <dd className="text-foreground-800">{article.readTime} 分钟</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">浏览</dt>
            <dd className="text-foreground-800">{formatViews(article.views)}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">整理者</dt>
            <dd className="text-foreground-800">{article.author}</dd>
          </div>
        </dl>
      </div>

      <div className="rounded-lg border border-accent-300 bg-accent-50 p-5">
        <h2 className="flex items-center gap-2 font-heading text-base font-bold text-accent-950">
          <i className="ri-links-line text-lg text-accent-700"></i>
          公开来源清单
        </h2>
        <p className="mt-1.5 text-xs text-accent-800">以下为本文整理所依据的公开信息类型</p>
        <ul className="mt-4 space-y-2.5">
          {article.evidence.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm leading-relaxed text-accent-900">
              <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-600"></i>
              {item}
            </li>
          ))}
        </ul>
      </div>

      {headings.length > 0 && (
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="font-heading text-base font-bold text-foreground-950">本文目录</h2>
          <ol className="mt-3 space-y-2">
            {headings.map((block, index) => (
              <li key={block.text}>
                <a
                  href={`#section-${index + 1}`}
                  className="flex items-start gap-2 text-sm leading-relaxed text-foreground-600 transition-colors hover:text-primary-600 cursor-pointer"
                >
                  <span className="font-heading text-xs font-bold text-primary-500">{String(index + 1).padStart(2, "0")}</span>
                  {block.text}
                </a>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="font-heading text-base font-bold text-foreground-950">相关整理</h2>
        <ul className="mt-3 flex flex-col">
          {related.map((item) => (
            <li key={item.id} className="border-b border-background-200 py-3 last:border-b-0 last:pb-0">
              <Link to={`/article/${item.id}`} className="group block cursor-pointer">
                <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                  {item.title}
                </p>
                <p className="mt-1 text-xs text-foreground-500">{item.date}</p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}